import { bopApi } from '@/lib/api/client';
import { MS_PER_DAY } from '@/utils/date';

export const ALERT_TYPES = ['alert', 'onboarding-alert', 'broker-alert'] as const;
export type AlertType = (typeof ALERT_TYPES)[number];

export type AlertSeverity = 'critical' | 'high' | 'medium' | 'low';
export type AlertStatus = 'active' | 'dismissed';

export const ALERT_TYPE_TITLES: Record<AlertType, string> = {
  alert: 'Compliance Alert',
  'onboarding-alert': 'Onboarding Review Alert',
  'broker-alert': 'Broker Review Alert',
};

// Onboarding reviews have a 7-day SLA; days_remaining is derived client-side from
// the alert creation time (legacy MAX_DAYS_FOR_REVIEW).
const MAX_DAYS_FOR_REVIEW = 7;

/** Raw Dynamo item shape from GET /v1/compliance/alerts */
interface RawComplianceAlert {
  /** partition key — the alert type */
  p: string;
  /** sort key — duplicate of alert_id */
  s: string;
  /** ISO-8601 creation timestamp */
  t: string;
  alert_id: string;
  event_type: string;
  severity: AlertSeverity;
  status?: AlertStatus;
  pid: string;
  [additionalData: string]: unknown;
}

/** UI shape: `p`→`entity`, `t`→`date_time`, `s` dropped, onboarding gains days_remaining */
export interface ComplianceAlert {
  alert_id: string;
  date_time: string;
  entity: string;
  event_type: string;
  pid: string;
  severity: AlertSeverity;
  status?: AlertStatus;
  days_remaining?: number;
  [additionalData: string]: unknown;
}

export interface BrokerAlert extends ComplianceAlert {
  broker_name: string;
  fee_band_id: string;
}

/** broker_fee_settlement_review / broker_fee_settlement_failed alerts — pid is the broker account holder UUID */
export interface FeeSettlementAlert extends ComplianceAlert {
  broker_name: string;
  settlement_id: string;
  /** whole-cent amount actually paid, e.g. '123.45' — null when broker_payment was malformed */
  payable_amount?: string | null;
  currency?: string;
  payment_reference?: string;
  failure_reason?: string;
}

export interface FuturesReviewAlert extends ComplianceAlert {
  key: string;
}

export interface RawAlertComment {
  s: string;
  alert_id: string;
  comment_id: string;
  comment: string;
  commented_by: string;
  /** ISO-8601 */
  commented_at: string;
  pid: string;
}

export interface AlertComment {
  comment_id: string;
  comment: string;
  commented_by: string;
  commented_at: string;
}

const toComplianceAlert = (raw: RawComplianceAlert, type: AlertType): ComplianceAlert => {
  const { p, s: _s, t, ...rest } = raw;
  return {
    ...rest,
    entity: p,
    date_time: t,
    ...(type === 'onboarding-alert' ? { days_remaining: MAX_DAYS_FOR_REVIEW - (Date.now() - Date.parse(t)) / MS_PER_DAY } : {}),
  };
};

/**
 * The API sorts by severity (critical→low) but its date tie-breaker is broken
 * (string subtraction → NaN → no-op), so same-severity order is arbitrary.
 * Onboarding reviews are instead ordered most-overdue first (legacy behavior).
 */
export const mapAlerts = (raw: RawComplianceAlert[], type: AlertType): ComplianceAlert[] => {
  const alerts = raw.map((item) => toComplianceAlert(item, type));
  if (type === 'onboarding-alert') {
    return alerts.sort((a, b) => (a.days_remaining ?? 0) - (b.days_remaining ?? 0) || Date.parse(b.date_time) - Date.parse(a.date_time));
  }
  return alerts;
};

// Newest first, sorted on the raw ISO timestamp (the legacy app sorted on a
// minute-precision formatted string, losing sub-minute ordering)
export const mapComments = (raw: RawAlertComment[]): AlertComment[] =>
  [...raw]
    .sort((a, b) => Date.parse(b.commented_at) - Date.parse(a.commented_at))
    .map(({ comment, comment_id, commented_at, commented_by }) => ({
      comment,
      comment_id: comment_id.replace('comment_', ''),
      commented_at,
      commented_by,
    }));

export const getComplianceAlerts = (type: AlertType, accountHolderId?: string) => {
  const searchParams = new URLSearchParams({ type });
  if (accountHolderId) searchParams.set('accountHolderId', accountHolderId);
  return bopApi.get('v1/compliance/alerts', { searchParams }).json<RawComplianceAlert[]>();
};

export const getAlertComments = (alertId: string) => bopApi.get(`v1/compliance/alerts/${alertId}/comments`).json<RawAlertComment[]>();

export const addAlertComment = (alertId: string, body: { comment: string; pid: string }) => bopApi.post(`v1/compliance/alerts/${alertId}/comment`, { json: body }).json();

/**
 * A dismiss succeeded but with a caveat when the 200 body is a JSON-encoded string —
 * e.g. "Alert dismissed, but unable to write audit records due to missing PID".
 * Callers must surface this: it signals a silent audit-trail failure.
 */
export const parseDismissWarning = (body: string): string | null => {
  if (!body) return null;
  try {
    const parsed: unknown = JSON.parse(body);
    return typeof parsed === 'string' && parsed ? parsed : null;
  } catch {
    return null;
  }
};

// Success responses can have an EMPTY body — never .json() these three.
export const dismissAlert = (alertId: string, type: AlertType, body: { comment: string; muteExpiry?: number }) =>
  bopApi
    .post(`v1/compliance/alerts/${alertId}/dismiss`, {
      searchParams: { type },
      json: { comment: body.comment, ...(body.muteExpiry ? { muteDuration: body.muteExpiry } : {}) },
    })
    .text();

export interface BulkDismissResult {
  success: Array<{ alertId: string; warning?: string }>;
  error: Array<{ alertId: string; error: string }>;
}

// bop-api rejects larger batches
export const BULK_DISMISS_MAX_ALERTS = 100;

// Partial failure comes back as HTTP 200 with entries in `error` — callers must check.
export const bulkDismissAlerts = (body: { alerts: Array<{ alertId: string; type: AlertType }>; comment: string; muteExpiry?: number }) =>
  bopApi
    .post('v1/compliance/alerts/dismiss', {
      json: { alerts: body.alerts, comment: body.comment, ...(body.muteExpiry ? { muteDuration: body.muteExpiry } : {}) },
    })
    .json<BulkDismissResult>();

export const updateBrokerFeeBandState = (body: {
  broker_account_id: string;
  fee_band_id: string;
  state: 'ACTIVE' | 'REJECTED';
  rejection_reason?: 'FEES_TOO_HIGH' | 'OTHER';
  comment: string;
}) => bopApi.post('v1/brokers/fee-bands/state', { json: body }).text();

export const FUTURES_REVIEW_KEY_PREFIX = 'futures-appropriateness_';

export const setFuturesManualReviewOutcome = (pid: string, body: { key: string; outcome: 'PASSED' | 'FAILED'; comment: string }) =>
  bopApi.post(`v1/user/${pid}/futures-assessment/manual-review`, { json: body }).text();

// Mute selections are labels in the UI; the wire value is an ABSOLUTE epoch-ms expiry
// (Date.now() + duration) — sending the bare duration silently creates an expired mute.
export const MUTE_DURATIONS = ['1 hour', '1 day', '1 month', '3 months', '6 months'] as const;
export type MuteDuration = (typeof MUTE_DURATIONS)[number];

export const MUTE_DURATIONS_MS: Record<MuteDuration, number> = {
  '1 hour': 60 * 60 * 1000,
  '1 day': MS_PER_DAY,
  '1 month': 30 * MS_PER_DAY,
  '3 months': 90 * MS_PER_DAY,
  '6 months': 180 * MS_PER_DAY,
};
